import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Scissors, Feather, Globe2, Warehouse, Quote } from "lucide-react";

/**
 * OurJourney — motivational company story anchoring the home page.
 * Renders once at the bottom of '/' before the Footer.
 */
export const OurJourney = () => {
  return (
    <section
      id="our-journey"
      data-testid="our-journey"
      className="relative bg-forest text-champagne overflow-hidden py-24 md:py-32 lg:py-40"
    >
      {/* Subtle grain / gold framing lines */}
      <div className="pointer-events-none absolute inset-x-6 md:inset-x-12 top-6 md:top-10 bottom-6 md:bottom-10 border border-champagne/10" />

      <div className="relative px-6 md:px-12 lg:px-16 max-w-6xl mx-auto">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="overline text-gold mb-6 flex items-center gap-3"
        >
          <span className="h-px w-8 bg-gold" /> § Our Journey
        </motion.div>

        {/* Editorial headline */}
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.25 }}
          transition={{ duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
          className="font-display text-champagne text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em]"
        >
          Woven in Mumbai.
          <span className="font-editorial italic text-gold block md:inline"> Made for the world.</span>
        </motion.h2>

        {/* Story paragraphs */}
        <div className="mt-10 md:mt-14 grid grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 md:col-span-7 space-y-5 text-champagne/85 text-base md:text-lg leading-relaxed"
          >
            <p>
              Anabiya Decor began the way every honest atelier begins — with a loom, a promise
              and a family standing behind it. Twenty years on, we still cut, stitch and finish
              every duvet, curtain and bath towel by hand in Mumbai, then ship them straight to
              hotels, resorts, yachts and warehouses <em className="text-gold not-italic">outside India</em>.
              We chose export-only for a reason: our craft belongs on beds and tables that
              expect the very best of India — without middlemen adding markups.
            </p>
            <p>
              Every stitch is led by <strong className="text-champagne">MDSR</strong> — founder,
              chief designer, and the reason our patterns feel a little quieter and a little
              more considered than the rest. Alongside Anabiya, MDSR also runs our sister
              atelier{" "}
              <span className="text-gold">Spne Design</span> — a small studio of pattern-cutters
              and monogram artists who help us keep every custom brief personal.
            </p>
          </motion.div>

          {/* Founder card */}
          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="col-span-12 md:col-span-5 border border-champagne/15 bg-forest/30 p-6 md:p-8 relative"
          >
            <Quote size={24} strokeWidth={1.2} className="text-gold absolute -top-3 -left-3 bg-forest p-1 rounded-full" />
            <p className="font-editorial italic text-champagne text-xl md:text-2xl leading-snug">
              “A hotel bed should feel like a whispered promise. We keep the promise —
              size after size, monogram after monogram, container after container.”
            </p>
            <div className="mt-6 pt-5 border-t border-champagne/15">
              <div className="font-display text-champagne text-lg leading-none">MDSR</div>
              <div className="overline text-gold/80 mt-2">Founder, CEO &amp; Head Designer</div>
              <div className="text-champagne/60 text-sm mt-1">Anabiya Decor · Spne Design · Mumbai</div>
            </div>
          </motion.aside>
        </div>

        {/* Four pillars — why customers choose us */}
        <div className="mt-16 md:mt-24">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="overline text-gold mb-6"
          >
            § Why the world chooses us
          </motion.div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {PILLARS.map((p, i) => (
              <motion.li
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.7, 0, 0.2, 1] }}
                data-testid={`journey-pillar-${p.slug}`}
                className="border border-champagne/15 p-5 md:p-6 bg-forest/30 hover:bg-forest/50 transition-colors duration-500"
              >
                <p.Icon size={22} strokeWidth={1.4} className="text-gold" />
                <div className="mt-4 font-display text-champagne text-2xl leading-tight">{p.title}</div>
                <p className="mt-2 text-champagne/70 text-sm leading-relaxed">{p.body}</p>
              </motion.li>
            ))}
          </ul>
        </div>

        {/* Export trust line */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mt-16 md:mt-20 border-t border-champagne/15 pt-10 md:pt-14 grid grid-cols-12 gap-6 md:gap-10 items-center"
        >
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-gold mb-3">§ Trusted worldwide · export only</div>
            <h3 className="font-display text-champagne text-3xl md:text-5xl leading-[1.05] tracking-[-0.01em]">
              Bulk-ready for
              <span className="text-gold"> hotels, resorts, yachts and warehouses</span> across
              <span className="italic font-editorial"> thirty countries.</span>
            </h3>
            <p className="mt-5 text-champagne/75 text-base md:text-lg leading-relaxed max-w-2xl">
              Minimum wholesale orders start at 100 pieces per SKU. We consolidate containers
              FOB Nhava Sheva and can ship air-freight when the launch date is closer than
              the sea route allows. Every order carries a same-day WhatsApp quote and a
              signed export invoice — no surprises, no middlemen.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <Link
              to="/shop"
              data-testid="journey-cta-shop"
              className="inline-flex items-center gap-3 overline text-forest bg-champagne px-6 py-4 rounded-full hover:bg-gold transition-colors duration-500"
            >
              Explore the wholesale catalogue →
            </Link>
            <div className="mt-4 text-champagne/60 text-xs leading-relaxed">
              Serving buyers in the US, UK, UAE, Saudi Arabia, France, Australia, Singapore
              and beyond — never sold within India.
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const PILLARS = [
  {
    slug: "custom",
    title: "Fully customisable",
    body: "Every size, colour, fabric, closure, header, monogram and thread — configurable on the product page or in a same-day custom brief.",
    Icon: Scissors,
  },
  {
    slug: "fabric",
    title: "Pure premium fabric",
    body: "Long-staple Egyptian & Giza cotton, French linen, silk-blend sateen and OEKO-TEX weaves — sourced direct from the mill, never rebranded.",
    Icon: Feather,
  },
  {
    slug: "export",
    title: "Trusted worldwide",
    body: "Export-only since day one. Shipped to hotels, resorts, yachts and warehouses across 30+ countries — never sold within India.",
    Icon: Globe2,
  },
  {
    slug: "bulk",
    title: "Bulk-ready in Mumbai",
    body: "Our Mumbai atelier runs full containers on schedule, with a live production dashboard, dedicated account manager and WhatsApp updates.",
    Icon: Warehouse,
  },
];
